/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";

import { db } from "../firebase";

const MONTH_NAMES = {
  "01": "January",
  "02": "February",
  "03": "March",
  "04": "April",
  "05": "May",
  "06": "June",
  "07": "July",
  "08": "August",
  "09": "September",
  10: "October",
  11: "November",
  12: "December",
};

function formatDateForDisplay(dateValue) {
  if (!dateValue || typeof dateValue !== "object") return "";

  const year = dateValue.year || "NA";
  const month = dateValue.month || "NA";
  const day = dateValue.day || "NA";

  if (year === "NA") return "";
  if (month === "NA") return year;

  const monthName = MONTH_NAMES[month] || month;

  if (day === "NA") {
    return `${monthName} ${year}`;
  }

  return `${String(Number(day))} ${monthName} ${year}`;
}

function displayLocation(form) {
  return (
    form?.locationName ||
    form?.location ||
    [form?.locationCity, form?.locationAdminArea, form?.locationCountry]
      .filter(Boolean)
      .join(", ")
  );
}

export default function ForumSpeciesPhotoCard({
  imageURL,
  speciesPhoto,
  speciesPhotoRef,
  onImageClick,
  compact = false,
}) {
  const [liveSpeciesPhoto, setLiveSpeciesPhoto] = useState(null);
  const [liveImageURL, setLiveImageURL] = useState("");

  useEffect(() => {
    const ownerUid = speciesPhotoRef?.ownerUid;
    const speciesId = speciesPhotoRef?.speciesId;

    if (!ownerUid || !speciesId) {
      setLiveSpeciesPhoto(null);
      setLiveImageURL("");
      return;
    }

    const speciesRef = doc(db, "users", ownerUid, "species", speciesId);

    const unsubscribe = onSnapshot(
      speciesRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setLiveSpeciesPhoto(null);
          setLiveImageURL("");
          return;
        }

        const speciesData = snapshot.data();
        const forms = Array.isArray(speciesData.forms) ? speciesData.forms : [];

        const matchingForm =
          forms.find(
            (candidate) =>
              speciesPhotoRef.formId &&
              candidate?.id === speciesPhotoRef.formId,
          ) ||
          forms.find(
            (candidate) =>
              speciesPhotoRef.formUrl &&
              candidate?.url === speciesPhotoRef.formUrl,
          );

        if (!matchingForm) {
          setLiveSpeciesPhoto(null);
          setLiveImageURL("");
          return;
        }

        setLiveSpeciesPhoto({
          speciesId: snapshot.id,
          scientificName: speciesData.name || "",
          commonName: speciesData.common || "",
          form: {
            id: matchingForm.id || "",
            label: matchingForm.label || "",
            date: matchingForm.date || null,

            location: matchingForm.location || "",
            locationName: matchingForm.locationName || "",
            locationLat: matchingForm.locationLat ?? null,
            locationLng: matchingForm.locationLng ?? null,
            locationCountry: matchingForm.locationCountry || "",
            locationAdminArea: matchingForm.locationAdminArea || "",
            locationCity: matchingForm.locationCity || "",
            locationPlaceId: matchingForm.locationPlaceId || "",

            variant: matchingForm.variant || "",
            notes: matchingForm.notes || "",
          },
        });

        setLiveImageURL(matchingForm.url || "");
      },
      () => {
        // The original observation may have been deleted or may not be
        // readable under the viewer's current collection permissions.
        // In either case, retain the stored forum snapshot.
        setLiveSpeciesPhoto(null);
        setLiveImageURL("");
      },
    );

    return () => unsubscribe();
  }, [
    speciesPhotoRef?.ownerUid,
    speciesPhotoRef?.speciesId,
    speciesPhotoRef?.formId,
    speciesPhotoRef?.formUrl,
  ]);

  const resolvedSpeciesPhoto = liveSpeciesPhoto || speciesPhoto || {};
  const resolvedImageURL = liveImageURL || imageURL || "";

  const scientificName = resolvedSpeciesPhoto.scientificName || "";
  const commonName = resolvedSpeciesPhoto.commonName || "";
  const form = resolvedSpeciesPhoto.form || {};

  const location = displayLocation(form);
  const date = formatDateForDisplay(form.date);

  const showForm =
    form.label && String(form.label).trim().toLowerCase() !== "default";

  const showVariant =
    form.variant && String(form.variant).trim().toLowerCase() !== "na";

  const image = (
    <img
      src={resolvedImageURL}
      alt={scientificName || commonName || ""}
      className={`block w-full object-contain ${
        compact ? "max-h-64" : "max-h-[460px]"
      }`}
    />
  );

  return (
    <article className="overflow-hidden border border-[#93CA87]/40 bg-white">
      <div
        className={`grid grid-cols-1 ${
          compact
            ? "sm:grid-cols-[260px_minmax(0,1fr)]"
            : "md:grid-cols-[minmax(260px,38%)_minmax(0,1fr)]"
        }`}
      >
        <div className="flex min-h-48 items-center justify-center bg-[#f4f6f8]">
          {onImageClick ? (
            <button
              type="button"
              onClick={() => onImageClick(resolvedImageURL)}
              aria-label="View species photo fullscreen"
              className="block h-full w-full cursor-zoom-in"
            >
              {image}
            </button>
          ) : (
            image
          )}
        </div>

        <div className="min-w-0 border-t border-[#93CA87]/30 p-4 sm:border-l sm:border-t-0">
          {(scientificName || commonName) && (
            <div className="border-b border-[#93CA87]/30 pb-3">
              <h3
                className={`leading-tight text-[#374049] ${
                  compact ? "text-2xl" : "text-3xl"
                }`}
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                {commonName || scientificName}

                {scientificName && commonName && (
                  <>
                    {" "}
                    <span className="italic">({scientificName})</span>
                  </>
                )}
              </h3>
            </div>
          )}

          <div className="mt-3 space-y-3">
            {location && location !== "Unknown" && (
              <div className="flex min-w-0 items-start gap-3">
                <div className="mt-0.5 shrink-0 text-slate-400">⌖</div>

                <div className="min-w-0">
                  <div className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                    Location
                  </div>

                  <div className="break-words text-xs leading-snug text-slate-700">
                    {location}
                  </div>
                </div>
              </div>
            )}

            {(date || showForm || showVariant) && (
              <div className="flex flex-wrap gap-2">
                {date && (
                  <div className="rounded-full border border-[#93CA87]/60 bg-[#374049] px-2.5 py-1 text-[11px] text-white shadow-sm">
                    <span className="text-[#24C3BE]">Date</span>
                    <span className="mx-1.5 text-slate-300">•</span>
                    {date}
                  </div>
                )}

                {showForm && (
                  <div className="rounded-full border border-[#93CA87]/60 bg-[#374049] px-2.5 py-1 text-[11px] text-white shadow-sm">
                    <span className="text-[#24C3BE]">Form</span>
                    <span className="mx-1.5 text-[#93CA87]">•</span>
                    {form.label}
                  </div>
                )}

                {showVariant && (
                  <div className="rounded-full border border-[#93CA87]/60 bg-[#374049] px-2.5 py-1 text-[11px] text-white shadow-sm">
                    <span className="text-[#24C3BE]">Variant</span>
                    <span className="mx-1.5 text-slate-300">•</span>
                    {form.variant}
                  </div>
                )}
              </div>
            )}

            {form.notes?.trim() && (
              <div className="border-t border-[#93CA87]/30 pt-3">
                <div className="flex min-w-0 items-start gap-3">
                  <div className="mt-0.5 shrink-0 text-[#24C3BE]">✎</div>

                  <div className="min-w-0 flex-1">
                    <div className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                      Notes
                    </div>

                    <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-relaxed text-slate-700 [overflow-wrap:anywhere]">
                      {form.notes}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
